export const fmt = (value) => {
  if (value === null || value === undefined || value === '') return '—';
  if (typeof value === 'number') return value.toLocaleString();
  return String(value);
};

// Cache the browser timezone label since it never changes during a session
// and Intl.DateTimeFormat is relatively expensive to instantiate.
let _browserTzShortLabel = null;
let _browserTzLongName = null;

export function browserTimezoneName() {
  if (_browserTzLongName) return _browserTzLongName;
  try {
    _browserTzLongName = Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  } catch (e) {
    _browserTzLongName = 'UTC';
  }
  return _browserTzLongName;
}

export function browserTimezoneLabel() {
  if (_browserTzShortLabel) return _browserTzShortLabel;
  try {
    // Use a stable reference date so the short label doesn't flip between
    // DST/standard time mid-page-render.
    const parts = new Intl.DateTimeFormat(undefined, {
      timeZoneName: 'short',
    }).formatToParts(new Date());
    const tz = parts.find((p) => p.type === 'timeZoneName');
    _browserTzShortLabel = tz?.value || browserTimezoneName();
  } catch (e) {
    _browserTzShortLabel = browserTimezoneName();
  }
  return _browserTzShortLabel;
}

/**
 * Format an ISO timestamp / Date in the user's browser timezone with a
 * trailing timezone label so it is unambiguous even if a server-provided
 * value was UTC or had a different offset.
 *
 * Pass `{ withTimezone: false }` to omit the trailing label.
 */
export const fmtDate = (value, options) => {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  const withTimezone = options?.withTimezone !== false;
  const local = d.toLocaleString();
  return withTimezone ? `${local} ${browserTimezoneLabel()}` : local;
};

export const fmtRelative = (value, nowMs = Date.now()) => {
  if (!value) return '';
  const d = new Date(value);
  const time = d.getTime();
  if (Number.isNaN(time)) return '';
  const diffMs = nowMs - time;
  const absMins = Math.floor(Math.abs(diffMs) / 60000);
  if (diffMs < 0) {
    if (absMins < 1) return 'in under a minute';
    if (absMins < 60) return `in ${absMins}m`;
    const hours = Math.floor(absMins / 60);
    if (hours < 48) return `in ${hours}h`;
    const days = Math.floor(hours / 24);
    return `in ${days}d`;
  }
  if (absMins < 1) return 'Just now';
  if (absMins < 60) return `${absMins}m ago`;
  const hours = Math.floor(absMins / 60);
  if (hours < 48) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
};

export const statusBadgeClass = (status) => {
  const normalized = String(status || 'unknown').toLowerCase();
  const map = {
    completed: 'success',
    running: 'primary',
    failed: 'danger',
    cancelled: 'secondary',
    queued: 'info',
    cancelling: 'warning',
  };
  return `ahri-dash-badge ahri-dash-badge--${map[normalized] || 'secondary'}`;
};
